import i18n from '../i18n';
import { getCosImageUrl } from './constants';
import { PetConfig } from '../store/usePetStore';

export type PetLifeState = 'normal' | 'hungry' | 'weak' | 'dying' | 'dead';

export interface PetLifeStateConfig {
  hungryAfterDays: number;
  weakAfterDays: number;
  dyingAfterDays: number;
  sleepAfterDays: number;
  hungryLabel?: string;
  weakLabel?: string;
  dyingLabel?: string;
  deadLabel?: string;
  skipWeekends?: boolean;
}

export interface PetLifeInfo {
  lifeState: PetLifeState;
  daysWithoutFeed: number;
  daysToNextState: number;
  daysToSleep: number;
}

export const defaultPetLifeStateConfig: PetLifeStateConfig = {
  hungryAfterDays: 3,
  weakAfterDays: 6,
  dyingAfterDays: 9,
  sleepAfterDays: 12,
  skipWeekends: true,
};

const isPositiveInteger = (value: unknown) => Number.isInteger(value) && Number(value) >= 1;

export const validatePetLifeStateConfig = (config: unknown): config is PetLifeStateConfig => {
  if (!config || typeof config !== 'object') return false;

  const candidate = config as Record<string, unknown>;
  const hungryAfterDays = Number(candidate.hungryAfterDays);
  const weakAfterDays = Number(candidate.weakAfterDays);
  const dyingAfterDays = Number(candidate.dyingAfterDays);
  const sleepAfterDays = Number(candidate.sleepAfterDays);

  return (
    isPositiveInteger(hungryAfterDays) &&
    isPositiveInteger(weakAfterDays) &&
    isPositiveInteger(dyingAfterDays) &&
    isPositiveInteger(sleepAfterDays) &&
    hungryAfterDays < weakAfterDays &&
    weakAfterDays < dyingAfterDays &&
    dyingAfterDays < sleepAfterDays
  );
};

export const parsePetLifeStateConfig = (value: unknown): PetLifeStateConfig => {
  if (typeof value !== 'string') return defaultPetLifeStateConfig;

  try {
    const parsed = JSON.parse(value);
    return validatePetLifeStateConfig(parsed) ? parsed : defaultPetLifeStateConfig;
  } catch {
    return defaultPetLifeStateConfig;
  }
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseDateInput = (value: string | Date) => {
  if (value instanceof Date) return value;

  const raw = String(value);
  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return direct;

  const normalized = raw.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?)(Z|[+-]\d{2}:?\d{2})?$/, '$1T$2$3');
  const fallback = new Date(normalized);
  return fallback;
};

export const getNaturalDaysDiff = (from: string | Date, to: string | Date = new Date(), skipWeekends = false) => {
  const fromDate = parseDateInput(from);
  const toDate = parseDateInput(to);

  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) return 0;

  const fromDay = startOfDay(fromDate);
  const toDay = startOfDay(toDate);

  if (!skipWeekends) {
    const diff = toDay.getTime() - fromDay.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  }

  // Count only weekdays (Mon-Fri)
  let count = 0;
  const cursor = new Date(fromDay);
  cursor.setDate(cursor.getDate() + 1); // start from next day
  while (cursor <= toDay) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

export const getPetLifeStateByDays = (daysWithoutFeed: number, config: PetLifeStateConfig): PetLifeState => {
  if (daysWithoutFeed < config.hungryAfterDays) return 'normal';
  if (daysWithoutFeed < config.weakAfterDays) return 'hungry';
  if (daysWithoutFeed < config.dyingAfterDays) return 'weak';
  if (daysWithoutFeed < config.sleepAfterDays) return 'dying';
  return 'dead';
};

export const derivePetLifeInfo = (
  lastFeedTime?: string | null,
  config: PetLifeStateConfig = defaultPetLifeStateConfig,
  fallbackLifeState?: PetLifeState | null,
) => {
  if (!lastFeedTime) {
    const lifeState = fallbackLifeState || 'normal';
    return {
      lifeState,
      daysWithoutFeed: 0,
      daysToNextState: lifeState === 'dead' ? 0 : config.hungryAfterDays,
      daysToSleep: lifeState === 'dead' ? 0 : config.sleepAfterDays,
    } satisfies PetLifeInfo;
  }

  const daysWithoutFeed = getNaturalDaysDiff(lastFeedTime, new Date(), !!config.skipWeekends);
  const calculatedState = getPetLifeStateByDays(daysWithoutFeed, config);

  // Use the more severe state between frontend calculation and backend fallback
  const stateOrder: PetLifeState[] = ['normal', 'hungry', 'weak', 'dying', 'dead'];
  const calcIdx = stateOrder.indexOf(calculatedState);
  const fallbackIdx = fallbackLifeState ? stateOrder.indexOf(fallbackLifeState) : -1;
  const lifeState = fallbackIdx > calcIdx ? fallbackLifeState! : calculatedState;

  const nextThresholdMap: Record<Exclude<PetLifeState, 'dead'>, number> = {
    normal: config.hungryAfterDays,
    hungry: config.weakAfterDays,
    weak: config.dyingAfterDays,
    dying: config.sleepAfterDays,
  };

  return {
    lifeState,
    daysWithoutFeed,
    daysToNextState: lifeState === 'dead' ? 0 : Math.max(0, (nextThresholdMap[lifeState as Exclude<PetLifeState, 'dead'>] ?? 0) - daysWithoutFeed),
    daysToSleep: lifeState === 'dead' ? 0 : Math.max(0, config.sleepAfterDays - daysWithoutFeed),
  } satisfies PetLifeInfo;
};

export const getPetLifeStateText = (lifeState: PetLifeState, config?: PetLifeStateConfig | null) => {
  if (config) {
    const labelMap: Record<string, string | undefined> = {
      hungry: config.hungryLabel,
      weak: config.weakLabel,
      dying: config.dyingLabel,
      dead: config.deadLabel,
    };
    if (labelMap[lifeState]) return labelMap[lifeState]!;
  }
  return i18n.t(`petLife.states.${lifeState}`);
};

export const getPetLifeStateHint = (lifeState: PetLifeState) => i18n.t(`petLife.hints.${lifeState}`);

export const getPetLifeStateStyle = (lifeState: PetLifeState) => {
  switch (lifeState) {
    case 'hungry':
      return {
        badge: 'bg-amber-100 text-amber-700 border-amber-200',
        filter: 'pet-life-filter-hungry',
        ring: 'ring-1 ring-amber-300/70',
      };
    case 'weak':
      return {
        badge: 'bg-slate-100 text-slate-700 border-slate-200',
        filter: 'pet-life-filter-weak',
        ring: 'ring-1 ring-slate-300/80',
      };
    case 'dying':
      return {
        badge: 'bg-orange-100 text-orange-700 border-orange-200',
        filter: 'pet-life-filter-dying',
        ring: 'ring-2 ring-orange-400/80 pet-life-warning-glow',
      };
    case 'dead':
      return {
        badge: 'bg-gray-100 text-gray-500 border-gray-200',
        filter: 'pet-life-filter-dead',
        ring: 'ring-2 ring-gray-300/70',
      };
    case 'normal':
    default:
      return {
        badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        filter: '',
        ring: '',
      };
  }
};

export const getPetName = (petId: string) => {
  const key = `pets.${petId}`;
  const translated = i18n.t(key);
  if (translated !== key) return translated;
  return i18n.t('pets.unknown');
};

export const getPetImage = (petId: string, level: number = 1, config: PetConfig[] = []) => {
  const petConfig = config.find(c => c.id === petId);
  if (!petConfig) {
    // Fallback if config not loaded or pet not found
    // We can try to guess or just return a default placeholder if needed.
    // For now, let's try to construct it if we have at least one config to guess the structure? 
    // No, without folder name we can't do much.
    // But maybe we can return a default egg or similar if config is missing?
    // Or we just return empty string and let the UI handle it.

    // However, the original code had a fallback to PET_OPTIONS[0].image
    // We don't have that anymore.
    return '';
  }

  const safeLevel = Math.max(1, Math.min(10, level || 1));
  return getCosImageUrl(petConfig.folder, `${safeLevel}.${petConfig.ext}`);
};

export const getPreviewImage = (petConfig: PetConfig, showMaxLevel: boolean = false) => {
  return getCosImageUrl(petConfig.folder, `${showMaxLevel ? 10 : 1}.${petConfig.ext}`);
};
